import React, { useState, useCallback, useRef, useEffect } from 'react';
import { makeAuthenticatedRequest } from '../services/api';
import { FaUpload, FaFilePdf, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaSpinner, FaHistory, FaUniversity } from 'react-icons/fa';

const BANK_ACCOUNTS = [
  { code: '11105', name: 'Beyond Bank Business Transaction Account' },
  { code: '11110', name: 'BB Cheque Account 2302' },
  { code: '12000', name: 'Business Savings Account' },
  { code: '12100', name: 'Business Savings Account - AU' },
  { code: '12110', name: 'Business Savings Account - NZ' },
];

const STATUS_STYLES = {
  matched:       { bg: 'bg-green-50',  border: 'border-green-200',  icon: FaCheckCircle,       color: 'text-green-600',  label: 'Ready to pay' },
  already_paid:  { bg: 'bg-blue-50',   border: 'border-blue-200',   icon: FaCheckCircle,       color: 'text-blue-600',   label: 'Already paid' },
  not_found:     { bg: 'bg-red-50',    border: 'border-red-200',    icon: FaTimesCircle,       color: 'text-red-600',    label: 'Not in Xero' },
  contact_mismatch:{ bg: 'bg-yellow-50', border: 'border-yellow-200', icon: FaExclamationTriangle,color: 'text-yellow-600', label: 'Different contact' },
  amount_mismatch:{ bg: 'bg-yellow-50', border: 'border-yellow-200', icon: FaExclamationTriangle,color: 'text-yellow-600', label: "Amount doesn't match" },
  ambiguous:     { bg: 'bg-orange-50', border: 'border-orange-200', icon: FaExclamationTriangle,color: 'text-orange-600', label: 'Multiple matches' },
  error:         { bg: 'bg-red-50',    border: 'border-red-200',    icon: FaTimesCircle,       color: 'text-red-600',    label: "Couldn't check Xero" },
};

// One line explaining an unmatched bill, per status. The supplier label is passed in so the
// wording reads naturally (e.g. "not Freedom FM") rather than exposing the internal supplier key.
const describeMatchIssue = (m, supplierLabel) => {
  switch (m.status) {
    case 'contact_mismatch':
      return `Invoice number ${m.normalizedRef} exists in Xero, but under ${m.otherContact} (${formatCurrency(m.otherAmount)}) — not ${supplierLabel}.`;
    case 'amount_mismatch':
      return `Xero has ${m.xeroInvoiceNumber || m.normalizedRef} for ${supplierLabel} at ${formatCurrency(m.xeroTotal)}, but the bill says ${formatCurrency(m.billAmount)}.`;
    case 'ambiguous':
      return `${(m.candidates || []).length} Xero invoices share this number and amount — can't pick one automatically.`;
    case 'not_found':
      return `No Xero invoice numbered ${m.normalizedRef}.`;
    case 'error':
      return m.error ? `Couldn't reach Xero for this bill: ${m.error}` : "Couldn't reach Xero for this bill.";
    default:
      return m.error || '';
  }
};

const formatCurrency = (n) => n != null ? `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const SUPPLIER_LABELS = {
  integrated_living: 'Integrated Living',
  freedom_fm: 'Freedom FM',
};

// A valid PDF may arrive with an empty or generic MIME type on some systems,
// so fall back to the extension rather than trusting file.type alone.
const isPdfFile = (f) => !!f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''));

const formatDuration = (ms) => {
  const totalSec = Math.floor((ms || 0) / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const newRunId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function SupplierPaymentsPage() {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  // Clear any in-flight timer/poller if the page unmounts mid-run.
  useEffect(() => () => {
    clearInterval(timerRef.current);
    clearInterval(pollRef.current);
  }, []);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bankAccount, setBankAccount] = useState('11110');
  const [paymentDate, setPaymentDate] = useState('');
  const [executing, setExecuting] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setSelected(new Set());
    setPaymentDate('');
    setReport(null);
    setError(null);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (isPdfFile(f)) {
      setFile(f);
      setError(null);
    } else {
      setError('Please select a PDF file.');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (isPdfFile(f)) {
      setFile(f);
      setError(null);
    } else {
      setError('Please drop a PDF file.');
    }
  };

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setError(null);

    // A run id lets the server report per-bill progress while the POST is in flight.
    const runId = newRunId();
    const startedAt = Date.now();
    setProgress({ processed: 0, total: null, elapsedMs: 0 });

    // Elapsed timer ticks locally; the poller fetches processed/total from the server.
    timerRef.current = setInterval(() => {
      setProgress((p) => (p ? { ...p, elapsedMs: Date.now() - startedAt } : p));
    }, 500);

    pollRef.current = setInterval(async () => {
      try {
        const pr = await makeAuthenticatedRequest(`/supplier-payments/progress/${runId}`, { method: 'GET' });
        setProgress((p) => (p ? { ...p, processed: pr.processed, total: pr.total } : p));
      } catch {
        // 404 until the run starts (or if it lands on another instance) — keep the timer running.
      }
    }, 1000);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('runId', runId);
      const res = await makeAuthenticatedRequest('/supplier-payments/preview', {
        method: 'POST',
        body: formData,
      });
      // makeAuthenticatedRequest unwraps the { status, message, data } envelope,
      // so `res` is already the data object ({ parsed, matches, summary, ... }).
      setPreview(res);
      // Default the payment date to the one printed on the remittance; the user can override it.
      setPaymentDate(res.parsed?.paymentDate || '');
      const initial = new Set(
        res.matches
          .filter((m) => m.status === 'matched')
          .map((m) => m.billRef)
      );
      setSelected(initial);
    } catch (err) {
      setError(err.message || 'Failed to parse remittance.');
    } finally {
      clearInterval(timerRef.current);
      clearInterval(pollRef.current);
      pollRef.current = null;
      timerRef.current = null;
      setProgress(null);
      setUploading(false);
    }
  }, [file]);

  const toggle = (ref) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };

  const toggleAll = () => {
    const payables = (preview?.matches || []).filter((m) => m.status === 'matched');
    if (selected.size === payables.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(payables.map((m) => m.billRef)));
    }
  };

  const handleExecute = useCallback(async () => {
    if (!preview || selected.size === 0) return;
    setExecuting(true);
    setError(null);
    try {
      // Send the selected payables plus the already-paid lines, so the report and
      // audit reflect the whole remittance. applyPayments only acts on status==='matched',
      // so already-paid lines are recorded but never re-paid.
      const selectedMatched = preview.matches.filter(
        (m) => m.status === 'matched' && selected.has(m.billRef)
      );
      const alreadyPaid = preview.matches.filter((m) => m.status === 'already_paid');
      const res = await makeAuthenticatedRequest('/supplier-payments/execute', {
        method: 'POST',
        body: {
          matches: [...selectedMatched, ...alreadyPaid],
          bankAccountCode: bankAccount,
          paymentDate,
          reference: preview.parsed?.remittanceNo || undefined,
        },
      });
      // Envelope already unwrapped: res is { runId, succeeded, alreadyPaid, failed }.
      setReport(res);
    } catch (err) {
      setError(err.message || 'Execution failed.');
    } finally {
      setExecuting(false);
    }
  }, [preview, selected, bankAccount, paymentDate]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await makeAuthenticatedRequest('/supplier-payments/history', { method: 'GET' });
      setHistory(res?.runs || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }, []);

  // ── Report view ──
  if (report) {
    const all = [...(report.succeeded || []), ...(report.alreadyPaid || []), ...(report.failed || [])];
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Payment Report</h1>
          <button onClick={reset} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Process Another
          </button>
        </div>

        {report.mock && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg flex items-center gap-3">
            <FaExclamationTriangle className="text-yellow-600" />
            <div>
              <p className="font-semibold text-yellow-800">TEST MODE — payments were mocked</p>
              <p className="text-sm text-yellow-700">No payments were written to Xero. Set TEST_MODE=false to apply real payments.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard label={report.mock ? 'Mocked' : 'Paid'} value={report.succeeded?.length || 0} color="green" />
          <StatCard label="Already Paid" value={report.alreadyPaid?.length || 0} color="blue" />
          <StatCard label="Failed" value={report.failed?.length || 0} color="red" />
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {all.map((r) => {
                const style = STATUS_STYLES[r.paymentStatus === 'success' ? 'matched' : r.paymentStatus === 'failed' ? 'error' : r.status] || STATUS_STYLES.error;
                const Icon = style.icon;
                return (
                  <tr key={r.billRef} className={`${style.bg}`}>
                    <td className="px-4 py-3 text-sm">{r.xeroInvoiceNumber || r.billRef}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(r.billAmount)}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`inline-flex items-center gap-1 ${style.color}`}>
                        <Icon size={14} />
                        {r.paymentStatus === 'success' ? (r.mock ? 'Paid (mock)' : 'Paid') : r.paymentStatus === 'failed' ? `Couldn't pay in Xero: ${r.error}` : style.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Preview view ──
  if (preview) {
    const payables = preview.matches.filter((m) => m.status === 'matched');
    const other = preview.matches.filter((m) => m.status !== 'matched');
    const supplierLabel = SUPPLIER_LABELS[preview.parsed?.supplier] || preview.parsed?.supplier;

    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Review Remittance</h1>
            <p className="text-sm text-gray-500 mt-1">
              {supplierLabel} — {preview.parsed?.bills?.length || 0} bill(s),{' '}
              {formatCurrency(preview.parsed?.totalAmount)}
            </p>
          </div>
          <button onClick={reset} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
            Start Over
          </button>
        </div>

        {preview.mock && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg flex items-center gap-3">
            <FaExclamationTriangle className="text-yellow-600" />
            <div>
              <p className="font-semibold text-yellow-800">Simulation mode — data is mocked</p>
              <p className="text-sm text-yellow-700">These matches are a fixture, not real Xero data. Paying records nothing.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
        )}

        {/* Payables */}
        {payables.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-700">
                Matched — {payables.length} invoice(s) ready to pay
              </h2>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.size === payables.length}
                  onChange={toggleAll}
                  className="rounded"
                />
                Select all
              </label>
            </div>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10"></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill Ref</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Xero Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Xero Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount Due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payables.map((m) => (
                    <tr key={m.billRef} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(m.billRef)}
                          onChange={() => toggle(m.billRef)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.billRef}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(m.billAmount)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{m.xeroInvoiceNumber}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${m.xeroStatus === 'AUTHORISED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {m.xeroStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(m.xeroAmountDue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Non-matchable */}
        {other.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">
              Unmatched — {other.length} bill(s) require attention
            </h2>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill Ref</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {other.map((m) => {
                    const style = STATUS_STYLES[m.status] || STATUS_STYLES.error;
                    const Icon = style.icon;
                    return (
                      <tr key={m.billRef} className={`${style.bg}`}>
                        <td className="px-4 py-3 text-sm font-medium">{m.billRef}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatCurrency(m.billAmount)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center gap-1 ${style.color}`}>
                            <Icon size={14} /> {style.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {describeMatchIssue(m, supplierLabel)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Execute bar */}
        {payables.length > 0 && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 rounded-b-lg shadow-lg">
            <div className="flex items-center justify-between max-w-6xl mx-auto">
              <div className="flex items-center gap-4">
                <FaUniversity className="text-gray-400" />
                <select
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {BANK_ACCOUNTS.map((a) => (
                    <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  Payment date
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {selected.size} of {payables.length} selected
                </span>
                <button
                  onClick={handleExecute}
                  disabled={selected.size === 0 || !paymentDate || executing}
                  className="px-6 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {executing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                  {executing ? 'Applying...' : `Pay ${selected.size} Invoice(s)`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Upload view (default) ──
  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Supplier Payment Matching</h1>
      <p className="text-gray-500 mb-6">
        Upload a supplier remittance PDF to match bill line items against Xero invoices, then apply payments in one click.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-lg shadow p-8">
        <label
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
        >
          <input type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />
          {file ? (
            <>
              <FaFilePdf className="text-red-500 text-3xl mb-2" />
              <span className="text-sm font-medium text-gray-700">{file.name}</span>
              <span className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</span>
            </>
          ) : (
            <>
              <FaUpload className="text-gray-400 text-3xl mb-2" />
              <span className="text-sm text-gray-500">Click or drag a remittance PDF here</span>
              <span className="text-xs text-gray-400 mt-1">Supported: Integrated Living, Freedom FM</span>
            </>
          )}
        </label>

        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="mt-4 w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload />}
          {uploading ? 'Parsing & matching...' : 'Upload & Match'}
        </button>

        {uploading && progress && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>
                {progress.total
                  ? `Processed ${progress.processed} of ${progress.total}`
                  : 'Parsing remittance…'}
              </span>
              <span className="tabular-nums">{formatDuration(progress.elapsedMs)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`bg-blue-600 h-2 rounded-full transition-all duration-500 ${progress.total ? '' : 'animate-pulse'}`}
                style={{ width: progress.total ? `${Math.round((progress.processed / progress.total) * 100)}%` : '100%' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* History link */}
      <div className="mt-6 text-center">
        <button
          onClick={loadHistory}
          className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
        >
          <FaHistory /> View recent payment runs
        </button>
      </div>

      {history && (
        <div className="mt-4 bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Recent Payment Runs</h3>
          </div>
          {history.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No payment runs yet.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Paid</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Already Paid</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Failed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.map((run) => (
                  <tr key={run.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">
                      {run.timestamp ? new Date(run.timestamp).toLocaleString() : '—'}
                      {run.mock && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-100 text-yellow-700 uppercase">Test</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm">{run.bankAccountCode}</td>
                    <td className="px-4 py-2 text-sm text-center text-green-600">{run.summary?.paid || 0}</td>
                    <td className="px-4 py-2 text-sm text-center text-blue-600">{run.summary?.alreadyPaid || 0}</td>
                    <td className="px-4 py-2 text-sm text-center text-red-600">{run.summary?.failed || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    green: 'bg-green-50 text-green-700 border-green-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[color] || colors.blue}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm opacity-75">{label}</div>
    </div>
  );
}
